/**
 * Pure matching of booked shifts to route instances. The only link is the route code a planner
 * typed into the shift's notes, and only an exact match is accepted. shifts.preassigned_route is
 * not used: it is RC1's, and resourcing nulls it 20 min after start without a timesheet.
 */

export type CodeTier =
  /** The note carries the route code, whole, as an aligned run of tokens. */
  | "exact"
  /** Every token of the code appears in the note in order, with noise between. */
  | "strong"
  /** Some but not all of the code's tokens. Ranks a candidate; never matches. */
  | "partial"
  | "none"

export interface MatchEvidence {
  codeTier: CodeTier
  codeFound: string | null
  codeTokenCount: number
  driverAgrees: boolean
  vehicleAgrees: boolean
  depotAgrees: boolean
  /** Whether the note's run number (-1, -2) matches this instance's; null when the note names none. */
  sequenceAgrees: boolean | null
  /** Comparable only within one instance's candidate set. */
  score: number
}

export interface MatchableInstance {
  id: string
  /** Instance code, e.g. "[COL]SIL-HGV-SIM-DR1-RL1-20260829-1". */
  code: string
  date: string
  depot: string
  driverId: string | null
  /** The mate on a double-manned route, as a person_id. */
  secondDriverId?: string | null
  /** Shifts this route needs; absent means 1. */
  crewSize?: number | null
  vehicleReg: string | null
  vehicleType: string | null
  plannedStart: string
  plannedEnd: string
}

export interface MatchableShift {
  id: string
  notes: string | null
  /** people.person_id (matches route instances' driver_id), not the people table's uuid. */
  personId: string | null
  vehicleReg: string | null
  /** Shift type: HivedVan, HivedHgv, HivedFlex, Dsp... */
  type: string | null
  /** Depot name in the shifts vocabulary, e.g. "Silvertown Depot". */
  location: string | null
  status: string
  startTime: string
  endTime: string
}

export interface ShiftMatch {
  instanceId: string
  shiftId: string
  evidence: MatchEvidence
  accepted: boolean
}

export interface AmbiguousInstance {
  instanceId: string
  shiftIds: string[]
  score: number
}

export interface MatchResult {
  matches: ShiftMatch[]
  unmatchedInstanceIds: string[]
  unmatchedShiftIds: string[]
  /** Top candidates tied: with exact matching, a duplicate booking of the same code. */
  ambiguous: AmbiguousInstance[]
}

/** Wide enough for an overnight HGV run booked the evening before, narrow enough to exclude the next day. */
const CANDIDATE_WINDOW_HOURS = 12

/** Strips the "[COL]" prefix and the instance "-YYYYMMDD-N" suffix, then splits into upper-case tokens. */
export function routeCodeTokens(raw: string | null | undefined): string[] {
  if (!raw) return []
  let s = String(raw).toUpperCase()
  s = s.replace(/^\s*\[?\s*COL\s*\]?\s*[-_:]?\s*/, "")
  s = s.replace(/[-_\s]*\b(20\d{6})\b([-_\s]*\d+)?\s*$/, "")
  return s
    .split(/[^A-Z0-9]+/)
    .filter((t) => t.length > 0)
}

export function normaliseRouteCode(raw: string | null | undefined): string {
  return routeCodeTokens(raw).join("-")
}

/** The run number ("-2" in "[COL]X-Y-20260907-2") that routeCodeTokens strips. */
export function routeCodeSequence(raw: string | null | undefined): number | null {
  if (!raw) return null
  const m = String(raw).match(/\b20\d{6}\b[-_\s]*(\d{1,2})\b/)
  return m ? Number(m[1]) : null
}

/** Drops "HIVED" and "Depot": the plan says "HIVED Silvertown Depot", a shift "Silvertown Depot". */
export function depotKey(raw: string | null | undefined): string {
  if (!raw) return ""
  return String(raw)
    .toUpperCase()
    .replace(/[^A-Z0-9\s]+/g, " ")
    .replace(/\bHIVED\b/g, " ")
    .replace(/\bDEPOT\b/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

/** Equal keys, or one ending in the other as whole words ("Oaklands Redditch" vs "Redditch"); not "contains". */
export function depotsAgree(a: string | null | undefined, b: string | null | undefined): boolean {
  const ka = depotKey(a)
  const kb = depotKey(b)
  if (!ka || !kb) return false
  if (ka === kb) return true
  return ka.endsWith(` ${kb}`) || kb.endsWith(` ${ka}`)
}

export function regKey(raw: string | null | undefined): string {
  if (!raw) return ""
  return String(raw).toUpperCase().replace(/[^A-Z0-9]+/g, "")
}

export function compareCodeToNotes(
  instanceCode: string,
  notes: string | null | undefined,
): { tier: CodeTier; codeFound: string | null; noteSequence: number | null } {
  const codeTokens = routeCodeTokens(instanceCode)
  if (codeTokens.length === 0) return { tier: "none", codeFound: null, noteSequence: null }

  const noteTokens = routeCodeTokens(notes)
  if (noteTokens.length === 0) return { tier: "none", codeFound: null, noteSequence: null }

  for (let i = 0; i + codeTokens.length <= noteTokens.length; i++) {
    let all = true
    for (let j = 0; j < codeTokens.length; j++) {
      if (noteTokens[i + j] !== codeTokens[j]) {
        all = false
        break
      }
    }
    if (all) {
      // Run number: the full date suffix, or a bare "-2" hyphened onto the code. Hyphen and single
      // digit both required, so "SIL-HGV-SIM-DR1-RL1 06:15" is not run six.
      const bare = new RegExp(`\\b${codeTokens[codeTokens.length - 1]}-([1-9])\\b`, "i")
      const noteSequence =
        routeCodeSequence(notes) ??
        (() => {
          const m = String(notes).match(bare)
          return m ? Number(m[1]) : null
        })()
      return { tier: "exact", codeFound: codeTokens.join("-"), noteSequence }
    }
  }

  let cursor = 0
  let matchedInOrder = 0
  for (const token of codeTokens) {
    const at = noteTokens.indexOf(token, cursor)
    if (at === -1) break
    cursor = at + 1
    matchedInOrder++
  }
  if (matchedInOrder === codeTokens.length) {
    return { tier: "strong", codeFound: codeTokens.join("-"), noteSequence: null }
  }

  const noteSet = new Set(noteTokens)
  const overlap = codeTokens.filter((t) => noteSet.has(t)).length
  if (overlap > 0) {
    return {
      tier: "partial",
      codeFound: codeTokens.filter((t) => noteSet.has(t)).join("-"),
      noteSequence: null,
    }
  }

  return { tier: "none", codeFound: null, noteSequence: null }
}

const TIER_SCORE: Record<CodeTier, number> = {
  exact: 100,
  strong: 70,
  partial: 20,
  none: 0,
}

/** The note identifies the route; driver, vehicle and depot only corroborate, never identify. */
export function scoreCandidate(
  instance: MatchableInstance,
  shift: MatchableShift,
): MatchEvidence {
  const { tier, codeFound, noteSequence } = compareCodeToNotes(instance.code, shift.notes)
  const codeTokenCount = routeCodeTokens(instance.code).length
  const instanceSequence = routeCodeSequence(instance.code)
  const sequenceAgrees =
    instanceSequence != null && noteSequence != null ? instanceSequence === noteSequence : null

  // Either crew member: on a double-manned route the second shift is booked to the mate.
  const shiftPerson = shift.personId?.trim()
  const driverAgrees =
    !!shiftPerson &&
    (instance.driverId?.trim() === shiftPerson || instance.secondDriverId?.trim() === shiftPerson)
  const vehicleAgrees =
    !!regKey(instance.vehicleReg) && regKey(instance.vehicleReg) === regKey(shift.vehicleReg)
  const depotAgrees = depotsAgree(instance.depot, shift.location)

  let score = TIER_SCORE[tier]
  // Longer codes score higher, so SIL-HGV cannot tie SIL-HGV-SIM-DR1-RL1 on a note carrying the latter.
  if (tier === "exact" || tier === "strong") score += Math.min(codeTokenCount, 8) * 5
  if (driverAgrees) score += 25
  if (vehicleAgrees) score += 15
  if (depotAgrees) score += 5
  // Naming the run outranks corroboration: it tells -1 from -2 when one driver could be on either.
  if (sequenceAgrees === true) score += 30

  return {
    codeTier: tier,
    codeFound,
    codeTokenCount,
    driverAgrees,
    vehicleAgrees,
    depotAgrees,
    sequenceAgrees,
    score,
  }
}

export function isAcceptable(evidence: MatchEvidence): boolean {
  if (evidence.codeTier !== "exact") return false
  if (evidence.sequenceAgrees === false) return false
  // A one-token code matches any note naming the depot, so it needs corroboration.
  const corroborated = evidence.driverAgrees || evidence.vehicleAgrees || evidence.depotAgrees
  return evidence.codeTokenCount >= 2 || corroborated
}

function hoursApart(a: string, b: string): number {
  return Math.abs(new Date(a).getTime() - new Date(b).getTime()) / 3_600_000
}

/** Each shift goes to at most one instance; instances settle best score first so a weak match cannot steal. */
export function matchShiftsToInstances(
  instances: MatchableInstance[],
  shifts: MatchableShift[],
): MatchResult {
  const matches: ShiftMatch[] = []
  const ambiguous: AmbiguousInstance[] = []
  const claimedShifts = new Set<string>()
  const settledInstances = new Set<string>()

  type Scored = { instance: MatchableInstance; shift: MatchableShift; evidence: MatchEvidence }
  const scored: Scored[] = []
  for (const instance of instances) {
    for (const shift of shifts) {
      if (hoursApart(shift.startTime, instance.plannedStart) > CANDIDATE_WINDOW_HOURS) continue
      const evidence = scoreCandidate(instance, shift)
      if (evidence.codeTier === "none" && !evidence.driverAgrees && !evidence.vehicleAgrees) continue
      scored.push({ instance, shift, evidence })
    }
  }

  const byInstance = new Map<string, Scored[]>()
  for (const entry of scored) {
    const list = byInstance.get(entry.instance.id) ?? []
    list.push(entry)
    byInstance.set(entry.instance.id, list)
  }
  for (const list of byInstance.values()) list.sort((a, b) => b.evidence.score - a.evidence.score)

  const order = [...byInstance.entries()].sort(
    (a, b) => (b[1][0]?.evidence.score ?? 0) - (a[1][0]?.evidence.score ?? 0),
  )

  for (const [instanceId, candidates] of order) {
    // A double-manned route is booked as two shifts carrying the same code.
    const wanted = Math.max(1, candidates[0]?.instance.crewSize ?? 1)
    let taken = 0

    while (taken < wanted) {
      const available = candidates.filter((c) => !claimedShifts.has(c.shift.id))
      const acceptable = available.filter((c) => isAcceptable(c.evidence))
      if (acceptable.length === 0) break

      let best = acceptable[0]
      let tied = acceptable.filter((c) => c.evidence.score === best.evidence.score)
      // A filled shift outranks an unfilled one carrying the same code.
      const filled = tied.filter((c) => c.shift.status === "Filled")
      if (filled.length > 0 && filled.length < tied.length) tied = filled
      if (tied.length === 1) best = tied[0]
      if (tied.length > 1) {
        const instance = best.instance
        const stillWanted = wanted - taken

        if (tied.length <= stillWanted) {
          // A double-manned route's own shifts tie by construction; take one and loop for the next.
          best = tied[0]
        } else {
          // Usually several runs (-1, -2) of the same code that day. Only tied shifts beyond the
          // seats those runs still need are duplicates; otherwise the nearest start takes this one.
          const code = normaliseRouteCode(instance.code)
          const slots =
            instances
              .filter(
                (i) =>
                  !settledInstances.has(i.id) &&
                  i.date === instance.date &&
                  normaliseRouteCode(i.code) === code,
              )
              .reduce((sum, i) => sum + Math.max(1, i.crewSize ?? 1), 0) - taken
          const byStart = [...tied].sort(
            (a, b) =>
              hoursApart(a.shift.startTime, instance.plannedStart) -
              hoursApart(b.shift.startTime, instance.plannedStart),
          )
          // The gap that matters is after the last shift this route would take, not after the first.
          const cut = stillWanted - 1
          const nearestIsClear =
            byStart.length <= stillWanted ||
            hoursApart(byStart[cut].shift.startTime, instance.plannedStart) <
              hoursApart(byStart[cut + 1].shift.startTime, instance.plannedStart)

          if (tied.length > slots || !nearestIsClear) {
            ambiguous.push({
              instanceId,
              shiftIds: tied.map((c) => c.shift.id),
              score: best.evidence.score,
            })
            // Settled but unmatched: the tied shifts stay unclaimed rather than risk the wrong one.
            settledInstances.add(instanceId)
            break
          }
          best = byStart[0]
        }
      }

      matches.push({
        instanceId,
        shiftId: best.shift.id,
        evidence: best.evidence,
        accepted: true,
      })
      claimedShifts.add(best.shift.id)
      taken++
    }

    // Settled only after claiming finishes: marking it mid-loop drops it from its own peer count.
    if (taken > 0) settledInstances.add(instanceId)
  }

  const unmatchedInstances = instances.filter((i) => !settledInstances.has(i.id))
  const unmatchedShifts = shifts.filter((s) => !claimedShifts.has(s.id))

  return {
    matches,
    unmatchedInstanceIds: unmatchedInstances.map((i) => i.id),
    unmatchedShiftIds: unmatchedShifts.map((s) => s.id),
    ambiguous,
  }
}
